import { bopApi } from '@/lib/api/client';
import { searchTransactions, type Transaction, type TransactionsSearchResponse } from '@/services/api/funds-transactions';
import { getPersonalIdentity, type PersonalIdentity } from '@/services/api/personal-identity';

// Hard cap. Deliberate: the cursor is not followed — the generate lambda
// re-fetches ALL transactions server-side, so the cap only limits what is listed for
// selection. When a cursor comes back we surface `hasMoreTransactions` so the operator
// knows older transactions are not shown (the funds cursor request contract is not
// documented in the spec, so paging was not guessed at).
export const TRANSACTIONS_MAX_PAGE_SIZE = 100;
// Only money moving between the client's account and the outside world is reportable: fees,
// instant-trade legs and other internal transfers are excluded SERVER-side — via this request
// param here AND again inside the generate lambda's own re-fetch (bop-api
// REPORTABLE_TRANSFER_METHODS, keep in sync). Never filter client-side only.
export const REPORTABLE_TRANSFER_METHODS = ['BLINC', 'BLOCKCHAIN', 'CARD', 'KINEXYS', 'SEPA', 'SEPA_INSTANT', 'SWIFT', 'WIRE'] as const;

export const NARRATIVE_MIN_LENGTH = 50;
// This copy is enforced three times (field rule, canGenerate, server zod) — keep in sync.
export const NARRATIVE_MIN_MESSAGE = 'Narrative must describe the case (min 50 characters)';
// The narrative is filed verbatim as the report's <action> element, which the goAML XSD caps at
// 4000 characters. bop-api reads the limit from the bundled XSD (narrativeLimits.js) — keep in sync.
export const NARRATIVE_MAX_LENGTH = 4000;
export const NARRATIVE_MAX_MESSAGE = `Narrative exceeds the FIU limit of ${NARRATIVE_MAX_LENGTH} characters for the report action text`;

export const isNarrativeLengthValid = (narrative: string) => narrative.length >= NARRATIVE_MIN_LENGTH && narrative.length <= NARRATIVE_MAX_LENGTH;
// Server zod requires each IP entry ≥ 3 chars — validated client-side on chip entry.
export const IP_MIN_LENGTH = 3;
export const ISO_COUNTRY_CODE_LENGTH = 2;

/** goAML identification codes (FIU reference table 5.5) */
export const IDENTIFICATION_TYPE_OPTIONS = [
  { label: 'Passport (PASP)', value: 'PASP' },
  { label: 'EU identity card (EIDK)', value: 'EIDK' },
  { label: 'Driving licence (RIJB)', value: 'RIJB' },
  { label: 'Foreigner ID (VRID)', value: 'VRID' },
] as const;

export const GENDER_OPTIONS = ['M', 'F'] as const;
export type Gender = (typeof GENDER_OPTIONS)[number];

/**
 * The full wire objects are passed through untouched — the shared funds
 * Transaction type covers every field the page consumes. Alias kept because the
 * fiu-utr feature components still import `FundsTransaction` from this module.
 */
export type FundsTransaction = Transaction;

// PersonalIdentity is owned by personal-identity.ts; re-exported for
// the fiu-utr feature components that import the type from this module.
export type { PersonalIdentity } from '@/services/api/personal-identity';

/** Sumsub-sourced overrides pre-mapped to goAML codes */
export interface SumsubClientDetails {
  /** Sumsub gender, expected 'M' | 'F' */
  gender?: string;
  birthPlace?: string;
  email?: string;
  identification?: {
    /** goAML identifier_type code */
    type?: string;
    number?: string;
    /** ISO alpha-2 */
    issueCountry?: string;
  };
}

export interface UtrIdentification {
  type: string;
  number: string;
  issueCountry: string;
}

/** Overrides for Sumsub-sourced values — undefined fields don't blank Sumsub data */
export interface UtrClientDetailsOverride {
  gender?: Gender;
  birthPlace?: string;
  email?: string;
  identification?: UtrIdentification;
}

export interface GenerateFiuUtrRequest {
  pid: string;
  narrative: string;
  transactionIds: string[];
  ips?: string[];
  caseRef?: string;
  clientDetails?: UtrClientDetailsOverride;
}

/** One entry per non-EUR transaction leg */
export interface UtrConversion {
  transactionNumber: string;
  currency: string;
  amount: number;
  rate: number;
  amountLocal: number;
}

export interface GenerateFiuUtrResponse {
  caseRef: string;
  /** `UTR-${caseRef}.xml` */
  fileName: string;
  /** S3 key — surfaced on the Compliance Reports page, unused here */
  fileKey: string;
  /** the full XML document — downloaded client-side */
  xml: string;
  conversions?: UtrConversion[];
  /** transactions filed with no IP at all — no request IP, no login shortly before, none entered */
  transactionsWithoutIp?: string[];
}

// The personal-identity `{statusCode, body}` envelope unwrap is owned by
// personal-identity.ts (shared getPersonalIdentity); null handling stays below.
export const getFiuTransactions = (pid: string): Promise<TransactionsSearchResponse> =>
  searchTransactions({
    account_holder_id: pid,
    max_page_size: TRANSACTIONS_MAX_PAGE_SIZE,
    transfer_method: REPORTABLE_TRANSFER_METHODS.join(','),
  });

export const getSumsubClientDetails = (pid: string) => bopApi.get(`v1/compliance/fiu-utr/client-details/${pid}`).json<SumsubClientDetails>();

export const generateFiuUtr = (body: GenerateFiuUtrRequest) => bopApi.post('v1/compliance/fiu-utr/generate', { json: body }).json<GenerateFiuUtrResponse>();

export interface FiuUtrSearchResult {
  transactions: Transaction[];
  /** the funds API returned a cursor — more than TRANSACTIONS_MAX_PAGE_SIZE transactions exist */
  hasMoreTransactions: boolean;
  /** null = no personal identity record → blocking warning, cannot generate */
  clientIdentity: PersonalIdentity | null;
  /** null = no Sumsub applicant → silent, override fields stay empty */
  clientDetails: SumsubClientDetails | null;
}

// The three parallel loads behind one search. Fix: the legacy BFF swallowed
// transactions fetch errors and returned undefined, TypeError-ing the page into a
// half-updated state — here a transactions failure rejects the whole search loudly and
// nothing below the lookup row updates.
export const searchFiuUtrClient = async (pid: string): Promise<FiuUtrSearchResult> => {
  const [transactionsResult, identityResult, detailsResult] = await Promise.allSettled([getFiuTransactions(pid), getPersonalIdentity(pid), getSumsubClientDetails(pid)]);
  if (transactionsResult.status === 'rejected') {
    throw transactionsResult.reason instanceof Error ? transactionsResult.reason : new Error('Failed to fetch transactions');
  }
  return {
    transactions: transactionsResult.value.transactions ?? [],
    hasMoreTransactions: Boolean(transactionsResult.value.cursor),
    clientIdentity: identityResult.status === 'fulfilled' ? identityResult.value : null,
    clientDetails: detailsResult.status === 'fulfilled' ? detailsResult.value : null,
  };
};

/** The override inputs as held in form state */
export interface ClientDetailsFormValues {
  gender: Gender | null;
  email: string;
  identificationType: string | null;
  identificationNumber: string;
  identificationIssueCountry: string;
}

export const EMPTY_CLIENT_DETAILS_FORM: ClientDetailsFormValues = {
  gender: null,
  email: '',
  identificationType: null,
  identificationNumber: '',
  identificationIssueCountry: '',
};

export interface SumsubPrefill {
  values: ClientDetailsFormValues;
  prefilledFromSumsub: boolean;
}

// Prefill rules: gender only when exactly 'M' or 'F'; nullish fields become null/''.
export const toClientDetailsPrefill = (details: SumsubClientDetails | null): SumsubPrefill => {
  if (!details) return { values: EMPTY_CLIENT_DETAILS_FORM, prefilledFromSumsub: false };
  return {
    values: {
      gender: details.gender === 'M' || details.gender === 'F' ? details.gender : null,
      email: details.email ?? '',
      identificationType: details.identification?.type ?? null,
      identificationNumber: details.identification?.number ?? '',
      identificationIssueCountry: details.identification?.issueCountry ?? '',
    },
    prefilledFromSumsub: Boolean(details.gender || details.email || details.identification),
  };
};

// Payload builder, reproduced exactly.
export const buildClientDetails = (values: ClientDetailsFormValues): UtrClientDetailsOverride | undefined => {
  // Keep: partial identification must NOT be sent — the API schema rejects it
  // (all-three-or-nothing); issueCountry is uppercased client-side.
  const identification: UtrIdentification | undefined =
    values.identificationType && values.identificationNumber && values.identificationIssueCountry.length === ISO_COUNTRY_CODE_LENGTH
      ? { type: values.identificationType, number: values.identificationNumber, issueCountry: values.identificationIssueCountry.toUpperCase() }
      : undefined;
  // Keep: only truthy fields included — the server merges defined fields over
  // Sumsub data, so an empty string would blank a Sumsub-sourced value.
  const details: UtrClientDetailsOverride = {
    ...(values.gender ? { gender: values.gender } : {}),
    ...(values.email ? { email: values.email } : {}),
    ...(identification ? { identification } : {}),
  };
  return Object.keys(details).length > 0 ? details : undefined;
};

export interface GeneratePayloadArgs {
  pid: string;
  narrative: string;
  ips: string[];
  transactionIds: string[];
  clientDetails: ClientDetailsFormValues;
}

// POST body — optional keys are omitted entirely when empty, never sent as [] / {}.
export const buildGeneratePayload = ({ pid, narrative, ips, transactionIds, clientDetails }: GeneratePayloadArgs): GenerateFiuUtrRequest => {
  const details = buildClientDetails(clientDetails);
  return {
    pid: pid.trim(),
    narrative,
    transactionIds,
    ...(ips.length > 0 ? { ips } : {}),
    ...(details ? { clientDetails: details } : {}),
  };
};
